import * as Immutable from 'immutable';
import * as t from 'io-ts';

import { Action, ImmutableInterface } from '../../types/';

export interface ActionCompetitions {
  selectedColumns?: Immutable.List<CompetitionColumn>;
  competitionType?: string;
  aggregationType?: string;
  isConsultant?: string;
  consultantType?: string;
  data?: any;
  country?: any;
  universityCountry?: any;
  level?: any;
  json?: any;
  competitionId?: string;
  aggregate?: any;
  __modalLeaderboardOptions?: any;
}

export interface BaseCompetitionsStore {
  competitions: CompetitionsStoreElement;
}
export type CompetitionsStore = ImmutableInterface<BaseCompetitionsStore>;

export type CompetitionColumnBase = {
  id: string;
  name: string;
  active: boolean;
  type: 'string' | 'number' | 'decimal' | 'flag';
  description?: string;
};

export type CompetitionColumn = ImmutableInterface<CompetitionColumnBase>;

export type OptionsBase = {
  user: Object;
  country: Object;
  university: Object;
  city: Object;
};

export type CompetitionsOptions = ImmutableInterface<OptionsBase>;

interface BaseCompetitionsStoreElement {
  selectedColumns: Immutable.List<string>;
  selectedColumnsModal: Immutable.List<string>;
  userCompetitions: CompetitionTable;
  consultantleader: CompetitionTable;
  hideAgreementModal: boolean;
  modalTableData: {
    count: number | null;
    next: any;
    previous: any;
    results: any[];
  };
  consultant: {
    honor: CompetitionTable;
    consultantleader: CompetitionTable;
  };
  isFetching: boolean;
  fetchUserCompetitions: boolean;
  fetchAgreeWithGuidelines: boolean;
  tableCompetitions: Immutable.Map<string, any>;
  tablePrizes: Immutable.Map<string, any>;
  tableReferrals: Immutable.Map<string, any>;
  tablePowerPool: Immutable.Map<string, any>;
  tableSpcLeaderboard: Immutable.Map<string, any>;
  fetchTableCompetitions: boolean;
  fetchPrizesTable: boolean;
  fetchReferralsTable: boolean;
  fetchPowerPoolTable: boolean;
  fetchSpcLeaderboardTable: boolean;
  fetchGuidelines: boolean;
  guidelines: any;
  currentCompetition: string | null;
  options: Immutable.List<CompetitionsOptions>;
  modalTableOptions: Immutable.Map<string, any>;
  countryOptions: Immutable.Map<string, string>;
  universityCountryOptions: Immutable.Map<string, string>;
  levelOptions: Immutable.Map<string, string>;
  fetchOptions: boolean;
  fetchOptionsId: string;
  fetchOptionsType: string;
  showGuidelinesModal: boolean;
  fetchAllCompetitions: boolean;
  allCompetitions: CompetitionTable;
  fetchCompetition: boolean;
  competition: any;
  aggregationChoices: Immutable.Map<string, any>;
  aggregation: string;
  logoutCompetition: string;
}
export type CompetitionsStoreElement = ImmutableInterface<BaseCompetitionsStoreElement>;

export type CompetitionsActionType = Action<ActionCompetitions>;

export type CompetitionTableBase = {
  count: number;
  next: any;
  previous: any;
  results: any[];
};

export type CompetitionTable = ImmutableInterface<CompetitionTableBase>;

export const CompetitionUser = t.partial({
  id: t.string,
  name: t.union([
    t.partial({
      firstName: t.string,
      lastName: t.string,
      fullName: t.string,
    }),
    t.null,
  ]),
  image: t.union([
    t.interface({
      url: t.string,
    }),
    t.null,
  ]),
});

export const Results = t.interface({
  id: t.string,
  name: t.string,
  countries: t.union([t.array(t.string), t.null]),
  status: t.string,
  teamBased: t.boolean,
  startDate: t.union([t.string, t.null]),
  endDate: t.union([t.string, t.null]),
  signUpStartDate: t.union([t.string, t.null]),
  signUpEndDate: t.union([t.string, t.null]),
  signUpDate: t.union([t.string, t.null]),
});

export const ConsultantLeaderboardResults = t.interface({
  user: t.union([t.string, CompetitionUser]),
  countries: t.string,
  city: t.string,
  university: t.string,
  actualWeight: t.number,
  actualWeightRank: t.number,
  actualAlphas: t.number,
  osISSharpeRatio: t.number,
  prodCorrelation: t.number,
  turnover: t.number,
});

export const ConsultantLeaderboardUserInfo = t.interface({
  count: t.number,
  next: t.union([t.string, t.null]),
  previous: t.union([t.string, t.null]),
  results: t.array(t.union([ConsultantLeaderboardResults, t.null])),
});

export const CompetitionUserInfo = t.interface({
  count: t.number,
  next: t.union([t.string, t.null]),
  previous: t.union([t.string, t.null]),
  results: t.array(t.union([Results, t.null])),
});

const ParametersOptional = t.partial({
  city: t.union([t.string, t.null]),
  value: t.number,
  level: t.union([t.string, t.null]),
  university: t.union([t.string, t.null]),
  team: t.union([t.string, t.null]),
  user: t.union([CompetitionUser, t.string, t.null]),
});

export const ResultsTable = t.interface({
  metaAlphas: t.union([t.number, t.null]),
  country: t.union([t.string, t.null]),
  rank: t.union([t.number, t.null]),
  score: t.union([t.number, t.null]),
  metaScore: t.union([t.number, t.null]),
  alphas: t.union([t.number, t.null]),
});

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const CompetitionTable = t.interface({
  results: t.array(t.union([ResultsTable, ParametersOptional, t.array(t.null)])),
  count: t.number,
  next: t.union([t.string, t.null]),
  previous: t.union([t.string, t.null]),
});

export const Options = t.interface({
  choices: t.array(t.interface({ label: t.string, value: t.string })),
});

export const CompetitionsLevels = t.array(
  t.interface({
    id: t.string,
    name: t.string,
  })
);

const AggregationChoices = t.interface({
  value: t.string,
  label: t.string,
  filters: t.array(t.string),
  properties: t.array(t.array(t.string)),
});

export const CompetitionOptions = t.interface({
  actions: t.interface({
    GET: t.interface({
      aggregate: t.interface({
        choices: t.array(AggregationChoices),
      }),
    }),
  }),
});
